import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { listAsyncRoleplaySessions } from "../../../axios/asyncRoleplay";
import type { AsyncRoleplaySession } from "../../../types/asyncRoleplay";
import {
  continueButtonStyles,
  dividerStyles,
  emptyHintStyles,
  loadingRowStyles,
  resumeItemStyles,
  resumeItemTextStyles,
  resumeLabelStyles,
  resumeListScrollStyles,
  resumeMetaStyles,
  resumeSectionStyles,
  resumeTitleStyles,
} from "./styles";

interface AsyncRoleplayResumeListProps {
  open: boolean;
  disabled?: boolean;
  onResume: (session: AsyncRoleplaySession) => void;
  onContentChange?: () => void;
}

const formatCreatedAt = (createdAt: number | null) => {
  if (!createdAt) return "";
  try {
    return new Date(createdAt * 1000).toLocaleString();
  } catch {
    return "";
  }
};

export const AsyncRoleplayResumeList: React.FC<AsyncRoleplayResumeListProps> = ({
  open,
  disabled = false,
  onResume,
  onContentChange,
}) => {
  const [sessions, setSessions] = useState<AsyncRoleplaySession[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const all = await listAsyncRoleplaySessions();
      const resumable = all.filter(
        (session) =>
          session.status === "active" ||
          session.status === "finishing" ||
          session.status === "finish_failed",
      );
      setSessions(resumable);
    } catch (loadError) {
      console.error("Failed to load async roleplay sessions:", loadError);
      setError("Could not load unfinished sessions.");
      setSessions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      void load();
    }
  }, [open, load]);

  useEffect(() => {
    if (!open || isLoading) return;
    onContentChange?.();
  }, [open, isLoading, sessions.length, onContentChange]);

  if (!open) return null;

  return (
    <Box sx={resumeSectionStyles}>
      <Typography sx={resumeLabelStyles}>
        Continue Voice Messaging roleplay
      </Typography>

      {isLoading && (
        <Box sx={loadingRowStyles}>
          <CircularProgress size={16} sx={{ color: "#7B49C1" }} />
          <Typography sx={emptyHintStyles}>Loading sessions...</Typography>
        </Box>
      )}

      {!isLoading && error && (
        <Typography sx={emptyHintStyles} color="error">
          {error}
        </Typography>
      )}

      {!isLoading && !error && sessions.length === 0 && (
        <Typography sx={emptyHintStyles}>
          No unfinished Voice Messaging sessions.
        </Typography>
      )}

      {!isLoading && sessions.length > 0 && (
        <Box sx={resumeListScrollStyles}>
          {sessions.map((session) => (
            <Box key={session.session_id} sx={resumeItemStyles}>
              <Box sx={resumeItemTextStyles}>
                <Typography sx={resumeTitleStyles}>
                  {session.window_title ||
                    (session.persona
                      ? `Roleplay with ${session.persona}`
                      : "Voice Messaging")}
                </Typography>
                <Typography sx={resumeMetaStyles}>
                  {session.status}
                  {session.created_at
                    ? ` · ${formatCreatedAt(session.created_at)}`
                    : ""}
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                disabled={disabled}
                onClick={() => onResume(session)}
                sx={continueButtonStyles}
              >
                Continue
              </Button>
            </Box>
          ))}
        </Box>
      )}

      <Box component="hr" sx={dividerStyles} />
    </Box>
  );
};
